import { MeetingTrackType } from '../interfaces/meeting-service.interface';

export class LocalTrackService {
  private static _instance: LocalTrackService;
  private localTracks: any[] = [];

  private constructor() {}

  public static get instance(): LocalTrackService {
    if (!LocalTrackService._instance) {
      LocalTrackService._instance = new LocalTrackService();
    }
    return LocalTrackService._instance;
  }

  get tracks(): any[] {
    return this.localTracks;
  }

  addTracks(tracks: any[]): void {
    this.localTracks.push(...tracks);
  }

  removeTrack(track: any): void {
    const index = this.localTracks.indexOf(track);
    if (index !== -1) {
      this.localTracks.splice(index, 1);
    }
  }

  async disposeTracks(typesToKeep: MeetingTrackType[] = []): Promise<void> {
    // Partition tracks, then ATOMICALLY claim them (reassign the array) before any
    // `await`. JS is single-threaded, so reassigning here — with no await between the
    // read and the write — guarantees that a second, concurrent disposeTracks() call
    // (e.g. a summon firing both the reactive CONNECTION_DISCONNECTED handler AND the
    // explicit switch_room leave) sees the already-emptied array and disposes nothing.
    //
    // Without this claim, two concurrent calls both iterate the same array, both dispose
    // the same audio track, and the second track.dispose() → conference.replaceTrack(track,
    // null) throws "Replace track failed - no transceiver" because the first call already
    // removed the transceiver.
    const tracksToKeep: any[] = [];
    const tracksToDispose: any[] = [];

    for (const track of this.localTracks) {
      const type = this.getTrackType(track);
      if (typesToKeep.includes(type)) {
        tracksToKeep.push(track);
      } else {
        tracksToDispose.push(track);
      }
    }

    // Claim synchronously: anything we're about to dispose is removed from the shared
    // array immediately so no concurrent caller can grab it.
    this.localTracks.length = 0;
    this.localTracks.push(...tracksToKeep);

    for (const track of tracksToDispose) {
      try {
        track.detach();
        const stream = track.getOriginalStream?.();
        if (stream) {
          stream.getTracks().forEach((t: MediaStreamTrack) => t.stop());
        }
        const native = track.getTrack?.();
        if (native?.readyState === 'live') native.stop();
        await track.dispose();
      } catch (error) {
        console.warn('[LocalTrackService] Error disposing track:', error);
      }
    }
  }

  async clearAll(): Promise<void> {
    await this.disposeTracks([]);
    this.localTracks = [];
  }

  public getTrackType(track: any): MeetingTrackType {
    if (track.getType() === 'audio') return MeetingTrackType.AUDIO;
    return track.videoType === 'desktop' ? MeetingTrackType.SCREEN : MeetingTrackType.VIDEO;
  }
}
